import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretSquareDown, faCaretSquareUp } from "@fortawesome/free-regular-svg-icons";
import { faArrowsAltH, faExclamation, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { sanitize } from "dompurify";
import { computed, ref, useAttrs } from "vue";
import { linkify } from "@/utils/utils";
import FormBoolean from "./Elements/FormBoolean.vue";
import FormColor from "./Elements/FormColor.vue";
import FormData from "./Elements/FormData/FormData.vue";
import FormDataUri from "./Elements/FormData/FormDataUri.vue";
import FormDataDialog from "./Elements/FormDataDialog.vue";
import FormDirectory from "./Elements/FormDirectory.vue";
import FormDrilldown from "./Elements/FormDrilldown/FormDrilldown.vue";
import FormHidden from "./Elements/FormHidden.vue";
import FormInput from "./Elements/FormInput.vue";
import FormNumber from "./Elements/FormNumber.vue";
import FormOptionalText from "./Elements/FormOptionalText.vue";
import FormRulesEdit from "./Elements/FormRulesEdit.vue";
import FormSelection from "./Elements/FormSelection.vue";
import FormTags from "./Elements/FormTags.vue";
import FormText from "./Elements/FormText.vue";
import FormUpload from "./Elements/FormUpload.vue";
import FormElementHelpMarkdown from "./FormElementHelpMarkdown.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormElement',
    props: {
        id: { default: "identifier" },
        type: null,
        value: null,
        title: null,
        refreshOnChange: { type: Boolean, default: false },
        help: null,
        helpFormat: { default: "html" },
        error: null,
        warning: null,
        disabled: { type: Boolean, default: false },
        loading: { type: Boolean },
        attributes: null,
        collapsedEnableText: { default: "Enable" },
        collapsedDisableText: { default: "Disable" },
        collapsedEnableIcon: { default: "far fa-caret-square-down" },
        collapsedDisableIcon: { default: "far fa-caret-square-up" },
        connectedEnableText: { default: "Remove connection from module." },
        connectedDisableText: { default: "Add connection to module." },
        connectedEnableIcon: { default: "fa fa-times" },
        connectedDisableIcon: { default: "fa fa-arrows-alt-h" },
        workflowBuildingMode: { type: Boolean, default: false }
    },
    emits: ["input", "change"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faExclamation, faTimes, faArrowsAltH, faCaretSquareDown, faCaretSquareUp);
        /** TODO: remove attrs computed.
         useAttrs is *not* reactive, and does not play nice with type safety.
         It is present for compatibility with the legacy "FormParameter" component,
         but should be removed as soon as that component is removed.
         */
        const attrs = computed(() => props.attributes || useAttrs());
        const collapsibleValue = computed(() => attrs.value["collapsible_value"]);
        const defaultValue = computed(() => attrs.value["default_value"]);
        const connectedValue = { __class__: "ConnectedValue" };
        const connected = ref(false);
        const collapsed = ref(false);
        const collapsible = computed(() => !props.disabled && collapsibleValue.value !== undefined);
        const connectable = computed(() => collapsible.value && Boolean(attrs.value["connectable"]));
        // Determines whether to expand or collapse the input
        {
            const valueJson = JSON.stringify(props.value);
            connected.value = valueJson === JSON.stringify(connectedValue);
            collapsed.value =
                connected.value ||
                    (collapsibleValue.value !== undefined && valueJson === JSON.stringify(collapsibleValue.value));
        }
        /** Submits a changed value. */
        function setValue(value) {
            emit("input", value, props.id);
            emit("change", props.refreshOnChange);
        }
        /** Handles collapsible toggle. */
        function onCollapse() {
            collapsed.value = !collapsed.value;
            connected.value = false;
            if (collapsed.value) {
                setValue(collapsibleValue.value);
            }
            else {
                setValue(defaultValue.value);
            }
        }
        /** Handles connected state. */
        function onConnect() {
            connected.value = !connected.value;
            collapsed.value = connected.value;
            if (connected.value) {
                setValue(connectedValue);
            }
            else {
                setValue(defaultValue.value);
            }
        }
        const isHidden = computed(() => attrs.value["hidden"]);
        const elementId = computed(() => `form-element-${props.id}`);
        const hasAlert = computed(() => alerts.value.length > 0);
        const showPreview = computed(() => (collapsed.value && attrs.value["collapsible_preview"]) || props.disabled);
        const showField = computed(() => !collapsed.value && !props.disabled);
        const isUriDataField = computed(() => {
            const dataField = props.type == "data";
            if (dataField && props.value && "src" in props.value) {
                const src = props.value.src;
                return src == "url";
            }
            return false;
        });
        const previewText = computed(() => attrs.value["text_value"]);
        const helpText = computed(() => {
            const helpArgument = attrs.value["argument"];
            if (helpArgument && !props.help?.includes(`(${helpArgument})`)) {
                return `${props.help} (${helpArgument})`;
            }
            else {
                return props.help;
            }
        });
        const currentValue = computed({
            get() {
                return props.value;
            },
            set(val) {
                setValue(val);
            },
        });
        const isHiddenType = computed(() => ["hidden", "hidden_data", "baseurl"].includes(props.type ?? "") ||
            (props.attributes && props.attributes.titleonly));
        const collapseText = computed(() => (collapsed.value ? props.collapsedEnableText : props.collapsedDisableText));
        const connectText = computed(() => (connected.value ? props.connectedEnableText : props.connectedDisableText));
        const isEmpty = computed(() => {
            if (currentValue.value === null || currentValue.value === undefined) {
                return true;
            }
            if (["text", "integer", "float", "password"].includes(props.type ?? "") && currentValue.value === "") {
                return true;
            }
            return false;
        });
        const isRequired = computed(() => attrs.value["optional"] === false);
        const isRequiredType = computed(() => props.type !== "boolean");
        const isOptional = computed(() => !isRequired.value && attrs.value["optional"] !== undefined);
        const formAlert = ref();
        const alerts = computed(() => {
            return [formAlert.value, props.error, props.warning]
                .filter((v) => v !== undefined && v !== null)
                .map((v) => linkify(sanitize(v, { USE_PROFILES: { html: true } })));
        });
        function onAlert(value) {
            formAlert.value = value;
        }
        return { __sfc: true, props, emit, attrs, collapsibleValue, defaultValue, connectedValue, connected, collapsed, collapsible, connectable, setValue, onCollapse, onConnect, isHidden, elementId, hasAlert, showPreview, showField, isUriDataField, previewText, helpText, currentValue, isHiddenType, collapseText, connectText, isEmpty, isRequired, isRequiredType, isOptional, formAlert, alerts, onAlert, FontAwesomeIcon, FormBoolean, FormColor, FormData, FormDataUri, FormDataDialog, FormDirectory, FormDrilldown, FormHidden, FormInput, FormNumber, FormOptionalText, FormRulesEdit, FormSelection, FormTags, FormText, FormUpload, FormElementHelpMarkdown };
    }
});
